<script setup>
import {ref, onMounted, computed} from 'vue'
import { getUser } from "@/services/api/auth.js"
import Dashboard from "@/components/dashboard/Dashboard.vue";
import {useI18n} from "vue-i18n";
import PageTitle from "@/components/common/PageTitle.vue";
import { getCurrentInstance } from 'vue'

const { appContext } = getCurrentInstance();
const user = ref(null)
const url = ref(null);
const dataDashboard = ref(null)
const { t } = useI18n()
const API_CONTRACT = appContext.config.globalProperties.URL_API_BASE_CONTRACT;

const breadcrumbs = computed(() => [
  { label: t('components.dashboard.contracts.pageTitle') },
])

onMounted(async () => {
    user.value = await getUser();
})

if (window.LIBERTY_MOCK) {
    // TODO URL VIRÁ DAS PREFERENCIAS DO USUARIO

    url.value = '/api/mock/dashboards?id=1';
} else {
    dataDashboard.value = {
        "id": 1,
        "widgets": [
            {
                "id": 1,
                "type": "ChartBar",
                "i18n": {
                    "pt": {
                        "title": "Quantidade de Contratos Ativos",
                        "information": "Exibe o número total de contratos que estão ativos no ERP, ou seja, instalados, em uso e não cancelados/suspensos. Representa a base atual de clientes ativos da empresa."
                    },
                    "en": {
                        "title": "Number of Active Contracts",
                        "information": "Displays the total number of contracts that are active in the ERP, meaning installed, in use, and not canceled/suspended. Represents the company’s current active client base."
                    },
                    "es": {
                        "title": "Cantidad de Contratos Activos",
                        "information": "Muestra el número total de contratos que están activos en el ERP, es decir, instalados, en uso y no cancelados/suspendidos. Representa la base actual de clientes activos de la empresa."
                    }
                },
                "style": {
                    "color": "#fbd30c"
                },
                "selected": true,
                "url_value": `${API_CONTRACT}/metric/name/StageStatus_Aprovado_Normal/history`
            },
            {
                "id": 2,
                "moneySymbol": true,
                "type": "ChartBar",
                "i18n": {
                    "pt": {
                        "title": "Valor de Contratos Ativos",
                        "information": "Exibe o valor financeiro (R$) dos contratos que estão ativos no ERP. Considera o " +
                            "valor total da carteira de clientes ativos em utilização."
                    },
                    "en": {
                        "title": "Active Contracts Value",
                        "information": "Displays the financial value (R$) of the contracts that " +
                            "are active in the ERP. It considers the total value of the portfolio of active clients in use."
                    },
                    "es": {
                        "title": "Valor de Contratos Activos",
                        "information": "Muestra el valor financiero (R$) de los contratos que están activos en el ERP. Considera " +
                            "el valor total de la cartera de clientes activos en utilización."
                    }
                },
                "style": {
                    "color": "#fbd30c"
                },
                "selected": false,
                "url_value": `${API_CONTRACT}/metric/name/StageStatus_Aprovado_Normal_ammount/history`
            },
            {
                "id": 3,
                "type": "ChartBar",
                "i18n": {
                    "pt": {
                        "title": "Quantidade de Contratos Cancelados",
                        "information": "Exibe o número total de contratos que estavam ativos e foram posteriormente cancelados no ERP." +
                            " Inclui clientes que já haviam passado pela instalação e utilização do serviço"
                    },
                    "en": {
                        "title": "Number of Canceled Contracts",
                        "information": "Displays the total number of contracts that were active and were later canceled in the ERP. " +
                            "It includes clients who had already gone through the installation and use of the service."
                    },
                    "es": {
                        "title": "Cantidad de Contratos Cancelados",
                        "information": "Muestra el número total de contratos que estaban activos y fueron posteriormente cancelados en el ERP. " +
                            "Incluye a los clientes que ya habían pasado por la instalación y utilización del servicio."
                    }
                },
                "style": {
                    "color": "#fbd30c"
                },
                "selected": false,
                "url_value": `${API_CONTRACT}/metric/name/StageStatus_Aprovado_Cancelado/history`
            },
            {
                "id": 4,
                "moneySymbol": true,
                "type": "ChartBar",
                "i18n": {
                    "pt": {
                        "title": "Valor de Contratos Cancelados",
                        "information": "Exibe o valor financeiro (R$) dos contratos que foram cancelados. O cálculo considera o " +
                            "valor dos contratos ativos até a data do cancelamento registrada no ERP."
                    },
                    "en": {
                        "title": "Canceled Contracts Value",
                        "information": "Displays the financial value (R$) of the contracts that were canceled. The calculation" +
                            " considers the value of the active contracts up to the cancellation date recorded in the ERP."
                    },
                    "es": {
                        "title": "Valor de Contratos Cancelados",
                        "information": "Muestra el valor financiero (R$) de los contratos que fueron cancelados. El cálculo considera" +
                            " el valor de los contratos activos hasta la fecha de cancelación registrada en el ERP."
                    }
                },
                "style": {
                    "color": "#fbd30c"
                },
                "selected": false,
                "url_value": `${API_CONTRACT}/metric/name/StageStatus_Aprovado_Cancelado_ammount/history`
            },
            {
                "id": 5,
                "type": "ChartBar",
                "i18n": {
                    "pt": {
                        "title": "Quantidade de Contratos Suspensos",
                        "information": "Exibe o número total de contratos que estavam ativos e foram temporariamente" +
                            " suspensos no ERP. A suspensão não encerra o contrato, o cliente permanece na base, mas com " +
                            "o serviço bloqueado até nova decisão da empresa."
                    },
                    "en": {
                        "title": "Number of Suspended Contracts",
                        "information": "Displays the total number of contracts that were active and were temporarily " +
                            "suspended in the ERP. The suspension does not terminate the contract; the client remains" +
                            " in the database, but with the service blocked until a new decision is made by the company."
                    },
                    "es": {
                        "title": "Cantidad de Contratos Suspendidos",
                        "information": "Muestra el número total de contratos que estaban activos y fueron suspendidos " +
                            "temporalmente en el ERP. La suspensión no finaliza el contrato; el cliente permanece en la " +
                            "base, pero con el servicio bloqueado hasta una nueva decisión de la empresa."
                    }
                },
                "style": {
                    "color": "#fbd30c"
                },
                "selected": false,
                "url_value": `${API_CONTRACT}/metric/name/StageStatus_Aprovado_Suspenso/history`
            },
            {
                "id": 6,
                "type": "ChartBar",
                "moneySymbol": true,
                "i18n": {
                    "pt": {
                        "title": "Valor de Contratos Suspensos",
                        "information": "Exibe o valor financeiro (R$) dos contratos que estão temporariamente suspensos." +
                            " Considera o valor dos contratos ativos até a suspensão, podendo voltar a ser" +
                            " contabilizados como ativos caso sejam reativados."
                    },
                    "en": {
                        "title": "Suspended Contracts Value",
                        "information": "Displays the financial value (R$) of the contracts that are temporarily " +
                            "suspended. It considers the value of the active contracts up to the suspension, and they " +
                            "may be counted again as active if reactivated."
                    },
                    "es": {
                        "title": "Valor de Contratos Suspendidos",
                        "information": "Muestra el valor financiero (R$) de los contratos que están temporalmente " +
                            "suspendidos. Considera el valor de los contratos activos hasta la suspensión, y pueden " +
                            "volver a contarse como activos en caso de ser reactivados."
                    }
                },
                "style": {
                    "color": "#fbd30c"
                },
                "selected": false,
                "url_value": `${API_CONTRACT}/metric/name/StageStatus_Aprovado_Suspenso_ammount/history`
            },
            {
                "id": 7,
                "type": "ChartBar",
                "i18n": {
                    "pt": {
                        "title": "Quantidade de Contratos em Bloqueio Administrativo",
                        "information": "Exibe o número total de contratos que estão em bloqueio administrativo no ERP." +
                            " Esses contratos permanecem na base da empresa, mas com o serviço interrompido " +
                            "temporariamente por decisão administrativa."
                    },
                    "en": {
                        "title": "Number of Contracts in Administrative Hold",
                        "information": "Displays the total number of contracts that are under administrative hold in " +
                            "the ERP. These contracts remain in the company’s database, but the service is temporarily " +
                            "suspended by administrative decision."
                    },
                    "es": {
                        "title": "Cantidad de Contratos en Bloqueo Administrativo",
                        "information": "Muestra el número total de contratos que están en bloqueo administrativo en " +
                            "el ERP. Estos contratos permanecen en la base de la empresa, pero el servicio está " +
                            "temporalmente suspendido por decisión administrativa."
                    }
                },
                "style": {
                    "color": "#fbd30c"
                },
                "selected": false,
                "url_value": `${API_CONTRACT}/metric/name/StageStatus_Aprovado_BloqueioAdministrativo/history`
            },
            {
                "id": 8,
                "type": "ChartBar",
                "i18n": {
                    "pt": {
                        "title": "Quantidade de Contratos em Bloqueio Financeiro",
                        "information": "Exibe o número total de contratos que estão em bloqueio financeiro no ERP. Esses contratos permanecem na base, mas com o serviço interrompido temporariamente devido a pendências financeiras do cliente."
                    },
                    "en": {
                        "title": "Number of Contracts in Financial Hold",
                        "information": "Displays the total number of contracts that are under financial hold in the ERP. These contracts remain in the database, but the service is temporarily suspended due to the client’s financial pending issues."
                    },
                    "es": {
                        "title": "Cantidad de Contratos en Bloqueo Financiero",
                        "information": "Muestra el número total de contratos que están en bloqueo financiero en el ERP. Estos contratos permanecen en la base, pero el servicio está temporalmente suspendido debido a pendientes financieros del cliente."
                    }
                },
                "style": {
                    "color": "#fbd30c"
                },
                "selected": false,
                "url_value": `${API_CONTRACT}/metric/name/StageStatus_Aprovado_BloqueioFinanceiro/history`
            }
        ],
    }
}
</script>

<template>
  <PageTitle :breadcrumbs="breadcrumbs" />
    <v-card class="sales-stats-card trezo-card p-0 border-radius d-block bg-white border-0 shadow-none vh-100">
        <v-row>
            <v-col cols="12 mt-3">
                <Dashboard :jsonData="dataDashboard" :url="url" />
            </v-col>
        </v-row>
    </v-card>
</template>

<style scoped>
.card-name {
    background-color: #b5b5b536!important;
}
</style>
